import { scanApi } from "#/api/scanApi";
import { CloseButton, Drawer, SegmentedControl, Skeleton, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconClock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const SavingsHero = () => {
  const { t } = useTranslation();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState<string>("product");

  const { data: savingsData, isFetching } = useQuery({
    queryKey: ["savings"],
    queryFn: async () => {
      const response = await scanApi.getSavings();
      return response.data;
    },
    staleTime: 1000 * 60,
  });

  const formatHours = (hours: number) => {
    if (hours >= 1000) {
      return (
        <>
          {(hours / 1000).toFixed(1)}
          <span className="text-2xl"> {t("dashboard.savings.thousandHrs")}</span>
        </>
      );
    }
    return (
      <>
        {Math.round(hours)}
        <span className="text-2xl"> {t("dashboard.savings.hrs")}</span>
      </>
    );
  };


  return (
    <>
      <div className="w-full flex flex-col gap-2">
        <div
          className="border rounded-lg p-8 w-full cursor-pointer hover:shadow-lg transition-shadow"
          style={{
            borderColor: `var(--mantine-color-indigo-3)`,
            backgroundColor: `var(--mantine-color-indigo-0)`,
          }}
          onClick={openDrawer}
        >
          {isFetching ? (
            <Skeleton height={120} />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <div
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: `var(--mantine-color-indigo-1)`,
                    }}
                  >
                    <IconClock
                      size={26}
                      style={{ color: `var(--mantine-color-indigo-7)` }}
                    />
                  </div>
                  <Title order={3} className="font-bold text-gray-800">
                    {t("dashboard.savings.title")}
                  </Title>
                </div>

                <div
                  className="text-4xl font-extrabold"
                  style={{ color: `var(--mantine-color-indigo-7)` }}
                >
                  {formatHours(savingsData?.hoursSaved || 0)}
                </div>

                <div className="text-sm text-gray-600">
                  {savingsData?.totalScans?.toLocaleString() || 0} {t("dashboard.savings.description")}
                  <span className="ml-2 text-indigo-600 font-medium">{t("dashboard.savings.clickToViewBreakdown")} →</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        position="bottom"
        size="xl"
        withCloseButton={false}
      >
        <div className="w-full flex flex-col gap-4">
          <div className="w-full flex items-center justify-between border-b border-b-slate-300 pb-4">
            <div className="flex items-center gap-2">
              <Title order={3}>{t("dashboard.savings.breakdownTitle")}</Title>
              <SegmentedControl
                data={[
                  { label: t("dashboard.savings.byCategory"), value: "product" },
                  { label: t("dashboard.savings.bySite"), value: "site" }
                ]}
                size="xs"
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>
            <CloseButton onClick={closeDrawer} />
          </div>

          {activeTab === "product" && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("dashboard.savings.category")}</Table.Th>
                  <Table.Th>{t("dashboard.savings.totalScans")}</Table.Th>
                  <Table.Th>{t("dashboard.savings.hoursSaved")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {savingsData?.byProduct?.map((item) => (
                  <Table.Tr key={item.productId}>
                    <Table.Td>{item.productName}</Table.Td>
                    <Table.Td>{item.scans.toLocaleString()}</Table.Td>
                    <Table.Td>{Math.round(item.hoursSaved)} {t("dashboard.savings.hrs")}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {activeTab === "site" && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("dashboard.savings.site")}</Table.Th>
                  <Table.Th>{t("dashboard.savings.totalScans")}</Table.Th>
                  <Table.Th>{t("dashboard.savings.hoursSaved")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {savingsData?.bySite?.map((item) => (
                  <Table.Tr key={item.siteId}>
                    <Table.Td>{item.siteName}</Table.Td>
                    <Table.Td>{item.scans.toLocaleString()}</Table.Td>
                    <Table.Td>{Math.round(item.hoursSaved)} {t("dashboard.savings.hrs")}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default SavingsHero;
